/// <reference types="vite/client" />

import { createEnvConfig } from "@ews-admin/global-design-system";
import { AcceptedLanguagesEnum } from "@/types/settings.definition";
import { TFunction } from "i18next";

export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const envConfig = createEnvConfig(import.meta.env.VITE_ENV, {
  ...(import.meta.env.VITE_MEDECINE360_BFF_URL && {
    bffUrl: import.meta.env.VITE_MEDECINE360_BFF_URL,
  }),
  ...(import.meta.env.VITE_MEDECINE360_LOGIN_BFF_URL && {
    loginBffUrl: import.meta.env.VITE_MEDECINE360_LOGIN_BFF_URL,
  }),
  ...(import.meta.env.VITE_MEDECINE360_APP_URL && {
    appUrl: import.meta.env.VITE_MEDECINE360_APP_URL,
  }),
  ...(import.meta.env.VITE_MEDECINE360_LOGIN_URL && {
    loginUrl: import.meta.env.VITE_MEDECINE360_LOGIN_URL,
  }),
  ...(import.meta.env.VITE_MEDECINE360_DASHBOARD_URL && {
    dashboardUrl: import.meta.env.VITE_MEDECINE360_DASHBOARD_URL,
  }),
  ...(import.meta.env.VITE_MEDECINE360_ADMIN_URL && {
    adminUrl: import.meta.env.VITE_MEDECINE360_ADMIN_URL,
  }),
});

export const getLoginApplicationUrl = (redirectTo?: string) => {
  const baseUrl = envConfig.loginUrl;
  if (redirectTo) {
    const url = new URL(baseUrl);
    url.searchParams.set("redirectTo", redirectTo);
    return url.toString();
  }
  return baseUrl;
};

export const getApplicationUrl = () => envConfig.appUrl;
export const getApplicationApiUrl = () => envConfig.bffUrl;
export const getLoginApiUrl = () => envConfig.loginBffUrl;
export const getDashboardUrl = () => envConfig.dashboardUrl;

export const getFieldsErrorMessage = (
  fields: { name: string; message: string }[]
): string => {
  return fields.map((field) => `${field.name}: ${field.message}`).join(", ");
};

export const getFirebaseApiKey = () => import.meta.env.VITE_FIREBASE_API_KEY;
export const getFirebaseAuthDomain = () => import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
export const getFirebaseProjectId = () => import.meta.env.VITE_FIREBASE_PROJECT_ID;
export const getFirebaseStorageBucket = () => import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
export const getFirebaseMessagingSenderId = () => import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
export const getFirebaseAppID = () => import.meta.env.VITE_FIREBASE_APP_ID;

export const PHONE_REGEX =
  /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;

export const getAcceptedLanguageCode = (currentLanguage: string) => {
  const language = Object.values(AcceptedLanguagesEnum).find(
    (language) => language.toLowerCase() === currentLanguage.toLowerCase()
  );
  return language || AcceptedLanguagesEnum.FR;
};

// Helper function to translate alert titles and descriptions
export const translateAlert = (
  alert: { title: string; description: string },
  t: TFunction
) => {
  // Map API alert titles to translation keys
  const titleTranslationMap: Record<string, string> = {
    "All Systems Operational": "dashboard.alerts.types.allSystemsOperational",
    "Maintenance Scheduled": "dashboard.alerts.types.maintenanceScheduled",
    "High Patient Volume": "dashboard.alerts.types.highPatientVolume",
    "Staff Shortage": "dashboard.alerts.types.staffShortage",
    "Equipment Issue": "dashboard.alerts.types.equipmentIssue",
    "Appointment Reminder": "dashboard.alerts.types.appointmentReminder",
    "System Update": "dashboard.alerts.types.systemUpdate",
    "Security Alert": "dashboard.alerts.types.securityAlert",
    "Licence Suspended": "dashboard.alerts.types.licenceSuspended",
    "Licence Expired": "dashboard.alerts.types.licenceExpired",
    "Licence Payment Pending": "dashboard.alerts.types.licencePaymentPending",
  };

  // Map API alert descriptions to translation keys
  const descriptionTranslationMap: Record<string, string> = {
    "Facility running smoothly": "dashboard.alerts.types.facilityRunning",
    "System maintenance planned": "dashboard.alerts.types.systemMaintenance",
    "Increased wait times expected":
      "dashboard.alerts.types.increasedWaitTimes",
    "Limited staff availability today":
      "dashboard.alerts.types.limitedAvailability",
    "Equipment under maintenance":
      "dashboard.alerts.types.equipmentMaintenance",
    "You have upcoming appointments":
      "dashboard.alerts.types.upcomingAppointments",
    "New features are available": "dashboard.alerts.types.newFeaturesAvailable",
    "Please review your security settings":
      "dashboard.alerts.types.reviewSecuritySettings",
    "The licence was suspended by the administration":
      "dashboard.alerts.types.licenceSuspendedDescription",
    "The licence has expired": "dashboard.alerts.types.licenceExpiredDescription",
    "The licence is awaiting payment":
      "dashboard.alerts.types.licencePaymentPendingDescription",
    "Some appointments have passed their scheduled time":
      "dashboard.alerts.types.overdueAppointmentsDescription",
    "Appointments scheduled within the next hour":
      "dashboard.alerts.types.upcomingNextHourDescription",
  };

  // Appointment alerts carry their count in the title ("3 Overdue Appointments")
  const overdue = /^(\d+) Overdue Appointments?$/.exec(alert.title);
  const upcoming = /^(\d+) Upcoming Appointments? in Next Hour$/.exec(alert.title);

  const translatedTitle = overdue
    ? t("dashboard.alerts.types.overdueAppointments", { count: Number(overdue[1]) })
    : upcoming
      ? t("dashboard.alerts.types.upcomingNextHour", { count: Number(upcoming[1]) })
      : titleTranslationMap[alert.title]
        ? t(titleTranslationMap[alert.title])
        : alert.title;

  const translatedDescription = descriptionTranslationMap[alert.description]
    ? t(descriptionTranslationMap[alert.description])
    : alert.description;

  return {
    title: translatedTitle,
    description: translatedDescription,
  };
};
